/**
 * Syncs overlay panel state with the DOM:
 * - Observes TanStack Query devtools panel and dispatches open state to Redux.
 * - Toggles body class when any left panel is open so CSS can hide the TanStack button.
 * - Sets tooltip (title) on the TanStack devtools button for consistency with other FABs.
 */

import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectLeftPanelOpen, useTranslation } from '~/app';
import { setTanstackDevtoolsOpen } from '~/overlay/actions';

const TANSTACK_BUTTON_SELECTOR = '.tsqd-parent-container .tsqd-open-btn';

const TANSTACK_PANEL_SELECTOR = '.tsqd-parent-container [class*="panel-position"]';
const POLL_MS = 300;

/**
 * Observes the TanStack Query devtools panel in the DOM and dispatches
 * open state to Redux (we don't control the devtools component).
 */
export function TanstackDevtoolsPanelObserver(): null {
  const dispatch = useDispatch();
  const lastOpenRef = useRef<boolean | null>(null);

  useEffect(() => {
    const check = () => {
      const el = document.querySelector(TANSTACK_PANEL_SELECTOR);
      const isOpen = el != null && el.getBoundingClientRect().width > 0;
      if (lastOpenRef.current !== isOpen) {
        lastOpenRef.current = isOpen;
        dispatch(setTanstackDevtoolsOpen(isOpen));
      }
    };

    const id = setInterval(check, POLL_MS);
    check();
    return () => clearInterval(id);
  }, [dispatch]);

  return null;
}

/**
 * Toggles body class when any left panel is open so global CSS can hide
 * the TanStack devtools button with the same animation as other left buttons.
 */
export function LeftPanelOpenBodyClass(): null {
  const leftPanelOpen = useSelector(selectLeftPanelOpen);

  useEffect(() => {
    document.body.classList.toggle('left-panel-open', leftPanelOpen);
    return () => document.body.classList.remove('left-panel-open');
  }, [leftPanelOpen]);

  return null;
}

const TANSTACK_ENTRANCE_DELAY_MS = 50;

/**
 * Sets the native title (tooltip) on the TanStack Query devtools button and
 * adds a body class to trigger the entrance animation (slide in from left),
 * matching the other left-side FABs.
 */
export function TanstackDevtoolsTooltipTitle(): null {
  const { t } = useTranslation();
  const hasTriggeredEntranceRef = React.useRef(false);
  const entranceTimeoutRef = React.useRef<number | null>(null);

  useEffect(() => {
    const applyTitleAndEntrance = () => {
      const el = document.querySelector(TANSTACK_BUTTON_SELECTOR);
      if (el instanceof HTMLElement) {
        el.setAttribute('title', t('tanstack_devtools_tooltip'));
        if (!hasTriggeredEntranceRef.current) {
          hasTriggeredEntranceRef.current = true;
          entranceTimeoutRef.current = window.setTimeout(() => {
            document.body.classList.add('tsqd-devtools-button-mounted');
            entranceTimeoutRef.current = null;
          }, TANSTACK_ENTRANCE_DELAY_MS);
        }
      }
    };

    applyTitleAndEntrance();
    const id = setInterval(applyTitleAndEntrance, 500);
    return () => {
      clearInterval(id);
      if (entranceTimeoutRef.current !== null) {
        clearTimeout(entranceTimeoutRef.current);
        entranceTimeoutRef.current = null;
      }
    };
  }, [t]);

  return null;
}
